import { useTranslation } from 'react-i18next';
import { useDocumentNavigate, useDocumentParams, useDocumentSearch } from 'app/router/params';
import { useCallback, useEffect, useState, FormEvent, ChangeEvent, ComponentProps } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { IconButton, Modal, DraggableTable } from 'app/components';
import LocaleBadge from 'app/components/layout/LocaleBadge';
import request from 'shared/lib/request';
import { emptyPage } from 'slate/extensions/page';

import { useUser, useSelectedOrganization } from 'app/state/contexts/UserContext';
import { usePermissions } from 'app/api/permission-queries';
import ServerErrorHandler from 'shared/feedback/ErrorHandler';
import RollForwardWizard from './RollForwardWizard/RollForwardWizard';
import { documentKeys, useExportDocument, useGetDocument, useGetDocumentVersions, useSectionReorder, SectionEntry, EditorDocument } from 'entities/document';
import authStore from 'shared/auth/authStore';
import { useQueryClient } from '@tanstack/react-query';
import { formatDate } from 'shared/lib/date';
import { Spinner } from 'app/components';
import { dispatchMessage, EventType } from 'shared/feedback/toasts';
import { SelectLanguageModal } from 'shared/overlays/LanguageModal';
import { useXhtmlValidation } from 'entities/xhtml';
import { api } from 'shared/api/client';
import { requireValue } from 'shared/lib/assert';

function validName(name: string) {
  return name.trim().length > 0 && name.trim().length < 65;
}

type OverviewVersion = NonNullable<ReturnType<typeof useGetDocumentVersions>['data']>[number];
type OverviewTableItem = ComponentProps<typeof DraggableTable>['items'][number];
type DocumentWithCompany = EditorDocument & { companyName: string };

interface Organization {
  id: number;
  name: string;
}

const Overview = () => {
  const { t } = useTranslation(['common', 'dashboard', 'overview', 'project-details', 'server']);

  const navigate = useDocumentNavigate();
  const { documentId } = useDocumentParams();
  const organizationId = useSelectedOrganization();
  const documentRevisionId = useDocumentSearch().revision;
  const { selectedOrganization, organizations } = useUser();
  const queryClient = useQueryClient();
  const { mutate: exportDocument, isPending: isExportingDocument } = useExportDocument();
  const { mutateAsync: getXhtmlValidationErrors } = useXhtmlValidation();
  const [sectionName, setSectionName] = useState('');
  const [versionName, setVersionName] = useState('');
  const [sectionToUpdate, setSectionToUpdate] = useState<SectionEntry>();
  const [sectionToDelete, setSectionToDelete] = useState<SectionEntry | null>(null);
  const [modalDeleteConfirmOpen, setModalDeleteConfirmOpen] = useState(false);
  const [versionSelectedForClone, setVersionSelectedForClone] = useState<OverviewVersion | null>(null);
  const [errorName, setErrorName] = useState(false);
  const [modalSectionOpen, setModalSectionOpen] = useState(false);
  const [modalVersionOpen, setModalVersionOpen] = useState(false);
  const [modalNewNameOpen, setModalNewNameOpen] = useState(false);
  const [modalNewLanguageOpen, setModalNewLanguageOpen] = useState(false);
  const [modalPdfLanguageOpen, setModalPdfLanguageOpen] = useState(false);
  const [modalCloneOpen, setModalCloneOpen] = useState(false);
  const [loadingExport, setLoadingExport] = useState(false);
  const [selectedVersion, setSelectedVersion] = useState<OverviewVersion | null>(null);
  const { documentPermissions, sectionPermissions, fetchPermissionData } = usePermissions(documentId);

  const revisionDocument = useGetDocument(documentId, selectedOrganization, documentRevisionId ?? undefined);
  const draftDocument = useGetDocument(documentId, selectedOrganization);
  const draftDocumentData = draftDocument.data;
  const versions = useGetDocumentVersions(documentId, documentRevisionId ?? undefined, draftDocumentData);
  const sectionReorder = useSectionReorder();
  const document = revisionDocument.data || draftDocumentData;
  const companyName = organizations?.find((organization: Organization) => selectedOrganization === organization.id)?.name ?? '';
  const documentData = document ? ({ ...document, companyName } as DocumentWithCompany) : undefined;

  const documentIsLoading = revisionDocument.isLoading || draftDocument.isLoading || versions.isLoading;
  const downloadPdf = ({ documentSuperId, documentRevisionId }: OverviewVersion, documentData: DocumentWithCompany, locale?: string) => {
    setLoadingExport(true);
    const url = `/gaby/pdf?documentSuperId=${documentSuperId}&documentRevisionId=${documentRevisionId}${locale ? '&locale=' + locale : ''}`;
    request
      .get(url)
      .then((res) => res.data)
      .then(({ data }: { data: string }) => {
        const element = window.document.createElement('a');
        element.setAttribute('href', `data:application/pdf;base64,${encodeURIComponent(data)}`);
        element.setAttribute('download', `${documentData.companyName} - ${documentData.name}.pdf`);
        element.style.display = 'none';
        window.document.body.appendChild(element);
        element.click();
        window.document.body.removeChild(element);
        setLoadingExport(false);
      })
      .finally(() => setLoadingExport(false));
  };

  const downloadXhtml = async ({ documentSuperId, documentRevisionId }: OverviewVersion, documentData: DocumentWithCompany, locale?: string) => {
    setLoadingExport(true);
    try {
      const url = `/gaby/xhtml?documentSuperId=${documentSuperId}&documentRevisionId=${documentRevisionId}${locale ? '&locale=' + locale : ''}`;
      const res = await request.get(url, { expectBinary: true });
      const contentType = res.headers?.['content-type'] || '';
      if (contentType.includes('application/zip')) {
        const blob = new Blob([res.data], { type: 'application/zip' });
        const downloadUrl = window.URL.createObjectURL(blob);
        const a = window.document.createElement('a');
        a.href = downloadUrl;
        a.download = `${documentData.companyName} - ${documentData.name}.zip`;
        a.click();

        window.URL.revokeObjectURL(downloadUrl);
        await getXhtmlValidationErrors(
          { documentRevisionId },
          {
            onSuccess: (data) => {
              if (data.validationErrors > 0) {
                dispatchMessage(EventType.ADD_MESSAGE, {
                  type: 'warning',
                  title: t('server:error.validation.title'),
                  text: `${t('server:error.validation.start')} ${data.validationErrors} ${t('server:error.validation.end')}`,
                });
              }
            },
          }
        );
      } else {
        const blob: Blob = res.data;
        const downloadUrl = window.URL.createObjectURL(blob);
        const a = window.document.createElement('a');
        a.href = downloadUrl;
        a.download = `${documentData.companyName} - ${documentData.name}.xhtml`;
        a.click();

        window.URL.revokeObjectURL(downloadUrl);
      }
    } catch (error) {
      ServerErrorHandler(error);
    } finally {
      setLoadingExport(false);
    }
  };

  useEffect(() => {
    if (versionSelectedForClone) {
      setModalCloneOpen(true);
    }
  }, [versionSelectedForClone]);

  const versionMenuItems = useCallback(
    (version: OverviewVersion, _altLocale?: string) => {
      const menu = [
        {
          title: t('project-details:named-version.download-pdf'),
          icon: 'file-pdf',
          onClick: () => documentData && downloadPdf(version, documentData),
        },
        {
          title: t('project-details:named-version.download-pdf-alt'),
          icon: 'file-pdf',
          onClick: () => {
            setSelectedVersion(version);
            setModalPdfLanguageOpen(true);
          },
        },
        {
          title: t('project-details:named-version.download-xhtml'),
          icon: 'file-code',
          onClick: () => documentData && downloadXhtml(version, documentData),
        },
      ];
      if (authStore.hasPermission('DOCUMENT_CLONE')) {
        menu.push({
          title: t('project-details:named-version.clone'),
          icon: 'clone',
          onClick: () => setVersionSelectedForClone(version),
        });
      }
      if (authStore.hasPermission('DOCUMENT_EXPORT')) {
        menu.push({
          title: t('project-details:named-version.export-json'),
          icon: isExportingDocument ? 'circle-notch fa-spin' : 'file-export',

          onClick: () => {
            if (!isExportingDocument) {
              exportDocument({
                documentSuperId: requireValue(version.documentSuperId, 'cannot export a version without a documentSuperId'),
                documentRevisionId: version.documentRevisionId,
                selectedOrganization,
                documentName: documentData?.name,
              });
            }
          },
        });
      }
      return menu;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedOrganization, isExportingDocument]
  );

  const sectionMenuItems = useCallback(
    (section: SectionEntry) => [
      {
        title: t('project-details:section.edit-name'),
        icon: 'pen',
        onClick: () => {
          setSectionToUpdate(section);
          setSectionName(section.name ?? '');
          setModalNewNameOpen(true);
        },
      },
      {
        title: t('project-details:section.view-draft'),
        icon: 'eye',
        onClick: () => {
          navigate({
            to: '/documents/$documentId/sections/$sectionId',
            params: { documentId, sectionId: String(section.superId) },
            search: {
              useDraft: true,
              ...(documentRevisionId ? { revision: documentRevisionId, sectionRevision: section.revisionId } : {}),
            },
          });
        },
      },
      {
        title: t('project-details:section.edit-lang'),
        icon: 'globe',
        onClick: () => {
          setSectionToUpdate(section);
          setModalNewLanguageOpen(true);
        },
      },
      {
        title: t('project-details:section.delete-section'),
        icon: 'trash',
        onClick: () => {
          setSectionToDelete(section);
          setModalDeleteConfirmOpen(true);
        },
      },
    ],
    [navigate, documentId, documentRevisionId, t]
  );

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    if (!validName(sectionName)) {
      setErrorName(true);
      return;
    }
    const revisionId = uuidv4();
    api
      .POST('/sections/', {
        body: {
          superId: uuidv4(),
          revisionId,
          content: [emptyPage(revisionId)],
          documentSuperId: Number(documentId),
        },
      })
      .then(({ data: section }) =>
        api.POST('/documents/{superId}/actions', {
          params: { path: { superId: Number(documentId) }, query: { compileSass: 'true' } },
          body: {
            action: 'create_section',
            data: {
              revisionId: section.revisionId,
              superId: section.superId,
              name: sectionName,
              locale: document?.content.locale,
            },
          },
        })
      )
      .then(() => {
        fetchPermissionData();
        setModalSectionOpen(false);
        setSectionName('');
        setErrorName(false);
        queryClient.invalidateQueries({
          queryKey: [...documentKeys.detailAll],
        });
      });
  };

  const handleNameChangeSubmit = (event: FormEvent) => {
    event.preventDefault();
    if (!validName(sectionName)) {
      setErrorName(true);
      return;
    }

    api
      .POST('/documents/{superId}/actions', {
        params: { path: { superId: Number(documentId) } },
        body: {
          action: 'update_section_name',
          revisionId: sectionToUpdate?.revisionId,
          data: {
            superId: sectionToUpdate?.superId,
            name: sectionName,
          },
        },
      })
      .then(() => {
        setModalNewNameOpen(false);
        setSectionName('');
        setErrorName(false);
        queryClient.invalidateQueries({
          queryKey: [...documentKeys.detailAll],
        });
      });
  };

  const handleDeleteSection = useCallback(() => {
    if (!sectionToDelete) return;
    api
      .POST('/documents/{superId}/actions', {
        params: { path: { superId: Number(documentId) } },
        body: {
          action: 'delete_section',
          revisionId: sectionToDelete.revisionId,
          data: {
            superId: sectionToDelete.superId,
          },
        },
      })
      .then(() => {
        setModalDeleteConfirmOpen(false);
        setSectionToDelete(null);
        queryClient.invalidateQueries({
          queryKey: [...documentKeys.detailAll],
        });
      })
      .catch(ServerErrorHandler);
  }, [sectionToDelete, documentId, queryClient]);

  const handleSubmitVersion = (event: FormEvent) => {
    event.preventDefault();
    if (!validName(versionName)) {
      setErrorName(true);
      return;
    }

    const draft = requireValue(draftDocumentData, 'named version created before the draft loaded');
    api
      .POST('/versions/{documentSuperId}', {
        params: { path: { documentSuperId: Number(documentId) } },
        body: {
          documentSuperId: Number(draft.superId),
          documentRevisionId: draft.revisionId,
          name: versionName,
        },
      })
      .then(() => {
        queryClient.invalidateQueries({
          queryKey: [...documentKeys.versionsAll],
        });
        setModalVersionOpen(false);
        setVersionName('');
      })
      .catch(ServerErrorHandler);
  };

  const handleSubmitLang = (event: string) => {
    api
      .POST('/documents/{superId}/actions', {
        params: { path: { superId: Number(documentId) } },
        body: {
          action: 'update_section_locale',
          revisionId: sectionToUpdate?.revisionId,
          data: {
            superId: sectionToUpdate?.superId,
            locale: event,
          },
        },
      })
      .then(() => {
        setModalNewLanguageOpen(false);
        queryClient.invalidateQueries({
          queryKey: [...documentKeys.detailAll],
        });
      })
      .catch(ServerErrorHandler);
  };

  const handleSubmitPdfLang = (event: string) => {
    if (!selectedVersion) return;
    setModalPdfLanguageOpen(false);
    if (documentData) downloadPdf(selectedVersion, documentData, event);
  };

  const handleVersionNameUpdate = (event: ChangeEvent<HTMLInputElement>) => {
    setVersionName(event.target.value);
  };

  const handleSelectVersion = (version: OverviewVersion) => {
    if (version.draft) {
      navigate({ search: (prev) => ({ ...prev, revision: undefined }) });
    } else {
      navigate({ search: (prev) => ({ ...prev, revision: version.documentRevisionId }) });
    }
  };

  const handleSectionNameUpdate = (event: ChangeEvent<HTMLInputElement>) => {
    setSectionName(event.target.value);
  };

  if (documentIsLoading || !document) {
    return <div>{t('common:loading-message')}</div>;
  }

  const altLocale = document.content.locale === 'sv-SE' ? 'en-US' : 'sv-SE';

  const closeCloneModal = () => {
    setModalCloneOpen(false);
    setVersionSelectedForClone(null);
  };

  if (versions.data?.[0]?.documentRevisionId) {
    console.log('PDF', window.location.origin + `/client/documents/${documentId}/pdf/${versions.data?.[0]?.documentRevisionId}`);
  }

  const handleCloseModal = () => {
    setSectionName('');
    setVersionName('');
    setErrorName(false);
    setModalNewNameOpen(false);
    setModalSectionOpen(false);
    setModalVersionOpen(false);
    setModalNewLanguageOpen(false);
    setModalPdfLanguageOpen(false);
  };

  const onReorderSections = (reorderedList: OverviewTableItem[]) => {
    sectionReorder.mutate({
      documentId,
      sectionOrder: reorderedList.map((s) => s.id),
    } as Parameters<typeof sectionReorder.mutate>[0]);
  };

  const sectionItems = document.content.sections.map((section: SectionEntry) => ({
    id: section.superId,
    title: section.name,
    detail: <LocaleBadge locale={section.locale} fallbackLocale={document.content.locale} />,
    disabled: !(sectionPermissions as Record<string, string>)[section.superId],
    dropdownItems: sectionMenuItems(section),
    linkTo: `/documents/${documentId}/sections/${section.superId}${
      documentRevisionId ? `?revision=${documentRevisionId}&sectionRevision=${section.revisionId}` : ''
    }`,
  }));
  const versionItems =
    versions.data?.map((version) => ({
      title: version.name,
      detail: formatDate(version.createdAt),
      dropdownItems: versionMenuItems(version, altLocale),
      onClick: () => handleSelectVersion(version),
      selected: version.selected,
    })) || [];

  return (
    <div className="project--overview">
      {loadingExport && (
        <div className="project--loading--container">
          <div className="project--loading--modal">
            <Spinner />
            <p>{t('project-details:download-document')}</p>
          </div>
        </div>
      )}
      <div className="columns two mb-2">
        <h2>{t('project-details:sections-list.title')}</h2>
        <div className="action--align--left">
          {draftDocumentData?.name === document?.name && documentPermissions.includes('ADMIN') && (
            <IconButton icon="plus" onClick={() => setModalSectionOpen(true)} />
          )}
        </div>
      </div>
      {!!document.content.sections.length ? null : <h4>{t('project-details:no-sections-message')}</h4>}
      <DraggableTable items={sectionItems} onSort={onReorderSections} documentSuperId={documentId} organizationId={organizationId} />
      <div className="columns two mb-2 mt-5">
        <h2>{t('project-details:title.named-versions')}</h2>
        <div className="action--align--left">
          {(documentPermissions.includes('ADMIN') || Object.values(sectionPermissions).some((p) => p === 'WRITE')) && (
            <IconButton icon="plus" onClick={() => setModalVersionOpen(true)} />
          )}
        </div>
      </div>
      <DraggableTable items={versionItems} />

      <Modal show={modalSectionOpen}>
        <div className="header d-flex">
          <h3>{t('project-details:new-section.modal.title')}</h3>
          <div className="close" onClick={handleCloseModal}>
            <i className="fa-regular fa-xmark" />
          </div>
        </div>
        <form onSubmit={handleSubmit} className={errorName ? 'form with-footer error' : 'form with-footer'}>
          <div className="mt-4">
            <label>
              <input className="form-control" type="text" ref={(o) => o?.focus()} value={sectionName} placeholder="" onChange={handleSectionNameUpdate} />
            </label>
            <span className="text-help">{errorName ? t('dashboard:new-project.modal.field.nameError') : ''}</span>
          </div>
          <div className="d-flex justify-content-end">
            <button type="button" className="button clear" onClick={handleCloseModal}>
              {t('common:button.cancel')}
            </button>
            <button type="submit" className="button primary">
              {t('common:button.save')}
            </button>
          </div>
        </form>
      </Modal>

      <Modal show={modalNewNameOpen}>
        <div className="header d-flex">
          <h3>{t('project-details:new-name.modal.title')}</h3>
          <div className="close" onClick={handleCloseModal}>
            <i className="fa-regular fa-xmark" />
          </div>
        </div>
        <div className="body ">
          <form onSubmit={handleNameChangeSubmit} className={errorName ? 'form with-footer error' : 'form with-footer'}>
            <div className="mt-4">
              <label>
                <input type="text" className="form-control" ref={(o) => o?.focus()} value={sectionName} placeholder="" onChange={handleSectionNameUpdate} />
              </label>
              <span className="text-help">{errorName ? t('dashboard:new-project.modal.field.nameError') : ''}</span>
            </div>
            <div className="d-flex justify-content-end">
              <button type="button" className="button clear" onClick={handleCloseModal}>
                {t('common:button.cancel')}
              </button>
              <button type="submit" className="button primary">
                {t('common:button.save')}
              </button>
            </div>
          </form>
        </div>
      </Modal>

      <Modal show={modalVersionOpen}>
        <div className="header d-flex">
          <h3>{t('project-details:new-version.modal.title')}</h3>
          <div className="close" onClick={handleCloseModal}>
            <i className="fa-regular fa-xmark" />
          </div>
        </div>
        <div className="body">
          <form onSubmit={handleSubmitVersion} className={errorName ? 'form with-footer error' : 'form with-footer'}>
            <div className="mt-4">
              <label>
                <input type="text" className="form-control" ref={(o) => o?.focus()} placeholder="" value={versionName} onChange={handleVersionNameUpdate} />
              </label>
              <span className="text-help">{errorName ? t('overview:new-version.modal.field.nameError') : ''}</span>
            </div>
            <div className="d-flex justify-content-end">
              <button type="button" className="button clear" onClick={handleCloseModal}>
                {t('common:button.cancel')}
              </button>
              <button type="submit" className="button primary">
                {t('common:button.save')}
              </button>
            </div>
          </form>
        </div>
      </Modal>

      <Modal show={modalDeleteConfirmOpen} onClose={() => setModalDeleteConfirmOpen(false)}>
        <div className="modal-body">{t('project-details:section.delete-section-text') + ` "${sectionToDelete?.name}"?`}</div>
        <div className="modal-footer">
          <button className="button clear" onClick={() => setModalDeleteConfirmOpen(false)}>
            {t('common:button.cancel')}
          </button>
          <button className="button danger" onClick={handleDeleteSection}>
            {t('project-details:section.delete-section')}
          </button>
        </div>
      </Modal>

      <SelectLanguageModal
        show={modalPdfLanguageOpen}
        onClose={handleCloseModal}
        title={t('project-details:new-lang.modal.title')}
        onSubmit={handleSubmitPdfLang}
        currentLocale={document.content.locale}
        confirmText={t('common:download')}
      />
      <SelectLanguageModal
        key={sectionToUpdate?.superId ?? 'default'}
        show={modalNewLanguageOpen}
        onClose={handleCloseModal}
        title={t('project-details:new-lang.modal.title')}
        onSubmit={handleSubmitLang}
        currentLocale={sectionToUpdate?.locale ?? document.content.locale}
        confirmText={t('common:confirm')}
      />
      <RollForwardWizard
        show={modalCloneOpen}
        onClose={closeCloneModal}
        documentVersion={versionSelectedForClone as ComponentProps<typeof RollForwardWizard>['documentVersion']}
        selectedOrganization={selectedOrganization}
      />
    </div>
  );
};

export default Overview;
